import { useState, useEffect } from "react";
import "./App.css";
import LOGO from "./assets/magicbell_logo.png";
import IMAGE from "./assets/main_image.svg";
import toast from "react-hot-toast";
import axios from "axios";

let BASE_URL = "https://magicbell.nexuslinkdev.com:3043";
// let BASE_URL = "http://localhost:8000";

const CREDENTIALS_KEY = "magicbell_saved_credentials";
const HISTORY_KEY = "magicbell_send_history";
const MAX_HISTORY = 10;

const loadJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
};

const saveJSON = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // storage unavailable (private browsing, quota, etc.) — safe to ignore
  }
};

const PLATFORM_FIELDS = [
  "imageUrl",
  "clickAction",
  "androidChannelId",
  "androidPriority",
  "androidSound",
  "iosBadge",
  "iosSound",
  "iosMutableContent",
  "iosCategory",
];

const pick = (obj, keys) => keys.reduce((acc, key) => ({ ...acc, [key]: obj[key] }), {});

const InfoTooltip = ({ text }) => (
  <span data-toggle="tooltip" data-placement="bottom" title={text}>
    {" "}
    ⓘ
  </span>
);

function App() {
  const [formData, setFormData] = useState(() => ({
    notificationType: "both",
    ...loadJSON(CREDENTIALS_KEY, {}),
  }));
  const [error, setError] = useState({});
  const [dataMode, setDataMode] = useState("simple");
  const [dataFields, setDataFields] = useState([{ key: "", value: "" }]);
  const [rawData, setRawData] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(() => loadJSON(HISTORY_KEY, []));

  useEffect(() => {
    saveJSON(CREDENTIALS_KEY, {
      serviceJson: formData.serviceJson || "",
      deviceToken: formData.deviceToken || "",
    });
  }, [formData.serviceJson, formData.deviceToken]);

  useEffect(() => {
    // data-toggle="tooltip" spans below rely on Bootstrap's JS plugin (jQuery + bootstrap.bundle.min.js
    // are already loaded in public/index.html) to turn their title attribute into a real tooltip
    window.$?.('[data-toggle="tooltip"]').tooltip();
  }, []);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const updateDataField = (idx, field, value) => {
    setDataFields((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addDataField = () => setDataFields((prev) => [...prev, { key: "", value: "" }]);

  const removeDataField = (idx) => setDataFields((prev) => prev.filter((_, i) => i !== idx));

  const parseTokens = (raw) =>
    (raw || "")
      .split("\n")
      .map((token) => token.trim())
      .filter(Boolean);

  const buildCustomData = () => {
    if (dataMode === "raw") {
      return rawData.trim() ? JSON.parse(rawData) : {};
    }
    return dataFields.reduce((acc, { key, value }) => {
      if (key.trim()) acc[key.trim()] = value;
      return acc;
    }, {});
  };

  const pushHistory = (entry) => {
    setHistory((prev) => {
      const next = [entry, ...prev].slice(0, MAX_HISTORY);
      saveJSON(HISTORY_KEY, next);
      return next;
    });
  };

  const removeHistoryEntry = (id) => {
    setHistory((prev) => {
      const next = prev.filter((item) => item.id !== id);
      saveJSON(HISTORY_KEY, next);
      return next;
    });
  };

  const clearHistory = () => {
    setHistory([]);
    saveJSON(HISTORY_KEY, []);
  };

  const reuseHistoryEntry = (entry) => {
    setFormData((prev) => ({
      ...prev,
      title: entry.title,
      message: entry.message,
      notificationType: entry.notificationType,
      dryRun: entry.dryRun,
      deviceToken: entry.deviceTokens.join("\n"),
      ...pick(entry, PLATFORM_FIELDS),
    }));
    setDataMode(entry.dataMode);
    setDataFields(entry.dataFields?.length ? entry.dataFields : [{ key: "", value: "" }]);
    setRawData(entry.rawData || "");
    toast.success("Loaded into the form — review and hit Send");
  };

  const validation = () => {
    let isError = false;
    let errors = {};

    const { serviceJson, deviceToken, title, message } = formData;

    if (!serviceJson) {
      errors = { ...errors, serviceJson: "please enter the service json !" };
      isError = true;
    } else {
      errors = { ...errors, serviceJson: "" };
    }
    if (!deviceToken || parseTokens(deviceToken).length === 0) {
      errors = { ...errors, deviceToken: "please enter at least one device token !" };
      isError = true;
    } else {
      errors = { ...errors, deviceToken: "" };
    }
    if (!title) {
      errors = { ...errors, title: "please enter the title !" };
      isError = true;
    } else {
      errors = { ...errors, title: "" };
    }
    if (!message) {
      errors = { ...errors, message: "please enter the message !" };
      isError = true;
    } else {
      errors = { ...errors, message: "" };
    }

    if (dataMode === "raw" && rawData.trim()) {
      try {
        JSON.parse(rawData);
        errors.rawData = "";
      } catch (e) {
        errors.rawData = "Invalid JSON";
        isError = true;
      }
    } else {
      errors.rawData = "";
    }

    setError(errors);

    return isError;
  };

  const onSubmit = async () => {
    if (validation()) return;

    const historyBase = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      title: formData.title,
      message: formData.message,
      notificationType: formData.notificationType,
      dryRun: !!formData.dryRun,
      deviceTokens: parseTokens(formData.deviceToken),
      dataMode,
      dataFields,
      rawData,
      ...pick(formData, PLATFORM_FIELDS),
    };

    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${BASE_URL}/notification`, {
        ...formData,
        data: buildCustomData(),
        deviceTokens: parseTokens(formData.deviceToken),
      });
      toast.success(response.data.message);
      setResult({ success: true, message: response.data.message, ...response.data.payload });
      pushHistory({ ...historyBase, success: true, resultMessage: response.data.message, results: response.data.payload?.results || null });
    } catch (err) {
      const message = err?.response?.data?.message || "something went wrong";
      toast.error(message);
      setResult({ success: false, message, ...err?.response?.data?.payload });
      pushHistory({ ...historyBase, success: false, resultMessage: message, results: null });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div class="container header">
        <img src={LOGO} alt="Logo" />
      </div>

      <div class="container-fluid">
        <div class="row main-container">
          <div class="col-md-6 illustration d-flex flex-column align-items-center">
            <img src={IMAGE} alt="Illustration" width={500} />
            <h1 className="text-center">Test Your Firebase Push Notifications with Ease!</h1>

            <details className="how-to-use" open>
              <summary>How to use this tool</summary>
              <ol>
                <li>
                  Get your Firebase service account JSON: Firebase Console → Project Settings → Service
                  Accounts → Generate new private key.
                </li>
                <li>
                  Get an FCM device/registration token from your app (the one your app receives from the
                  Firebase SDK on the test device). Paste one per line to send to multiple devices at
                  once — the result panel shows a pass/fail per token.
                </li>
                <li>Fill in Title &amp; Message — shown whenever Notification Type is "Notification" or "Both".</li>
                <li>
                  Optionally add custom data: use Key/Value for quick fields, or Raw JSON if you need
                  nested values or many fields at once.
                </li>
                <li>
                  Choose Notification Type:
                  <ul>
                    <li><b>Data only</b> — nothing shown automatically; use this to test your app's own message handling.</li>
                    <li><b>Notification only</b> — the OS shows it automatically; use this to check how the banner looks.</li>
                    <li><b>Both</b> — visible notification and custom data delivered together, closest to a real push.</li>
                  </ul>
                </li>
                <li>
                  Check "Dry run" to validate the payload without actually delivering it — useful to
                  confirm your setup is correct before spamming a real device.
                </li>
                <li>
                  Click Send. The result panel below the button shows a pass/fail per device token, the
                  FCM error code on failure (so you can tell "bad token/service account" from "my app
                  didn't handle it"), and the exact JSON sent — compare it against what your app actually
                  received.
                </li>
                <li>
                  Open "Platform-specific options" to set an image, a click action/deep link, or
                  Android/iOS-only fields (channel, priority, sound, badge, category) — useful for
                  testing exactly what your app's notification handler expects.
                </li>
                <li>
                  Your Service JSON and device token(s) are saved in this browser only (never sent
                  anywhere but your own backend) so you don't have to re-paste them every time. Recent
                  sends show up below with a "Reuse" button to reload them into the form.
                </li>
              </ol>
            </details>
          </div>

          <div class="col-md-6">
            <div class="form-container">
              <h4>Simple interface to Test Mobile Push Notification</h4>
              <form>
                <div class="form-group">
                  <label for="serviceJson">
                    Service Json
                    <InfoTooltip text="Project setting > Service account > Generate new private key" />{" "}
                    <span className="error">*</span>
                  </label>
                  <textarea
                    type="text"
                    rows={8}
                    class="form-control"
                    id="serviceJson"
                    placeholder="Service Json"
                    value={formData?.serviceJson}
                    onChange={(e) => handleChange("serviceJson", e.target.value)}
                  />
                  <p className="error-msg">{error?.serviceJson}</p>
                </div>
                <div class="form-group">
                  <label for="deviceToken">
                    FCM Registration Token(s)
                    <InfoTooltip text="One token per line to send the same notification to multiple devices at once" />{" "}
                    <span className="error">*</span>
                  </label>
                  <textarea
                    rows={3}
                    class="form-control"
                    id="deviceToken"
                    placeholder="One device token per line"
                    value={formData?.deviceToken}
                    onChange={(e) => handleChange("deviceToken", e.target.value)}
                  />
                  <p className="error-msg">{error?.deviceToken}</p>
                </div>
                <div class="form-group">
                  <label for="title">
                    Title
                    <InfoTooltip text="The main heading shown in the notification banner" />{" "}
                    <span className="error">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control"
                    id="title"
                    placeholder="Notification Title"
                    value={formData?.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                  />
                  <p className="error-msg">{error?.title}</p>
                </div>
                <div class="form-group">
                  <label for="message">
                    Message
                    <InfoTooltip text="The body text shown below the title in the notification banner" />{" "}
                    <span className="error">*</span>
                  </label>
                  <input
                    class="form-control"
                    id="message"
                    placeholder="Notification Message"
                    value={formData?.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                  ></input>
                  <p className="error-msg">{error?.message}</p>
                </div>

                <div className="form-group">
                  <label htmlFor="notificationType">
                    Notification Type
                    <InfoTooltip text="Data: silent, app handles it. Notification: OS shows it, no custom data. Both: visible notification + custom data." />
                  </label>
                  <select
                    className="form-control"
                    id="notificationType"
                    value={formData?.notificationType}
                    onChange={(e) => handleChange("notificationType", e.target.value)}
                  >
                    <option value="data">Data only (silent, app handles display)</option>
                    <option value="notification">Notification only (OS shows it, no custom data)</option>
                    <option value="both">Both (visible notification + custom data)</option>
                  </select>
                </div>

                <div className="form-group form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="dryRun"
                    checked={!!formData?.dryRun}
                    onChange={(e) => handleChange("dryRun", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="dryRun">
                    Dry run — validate the payload without actually delivering it
                  </label>
                </div>

                <details className="platform-options">
                  <summary>Platform-specific options (optional)</summary>

                  <div className="form-group">
                    <label htmlFor="imageUrl">
                      Image URL
                      <InfoTooltip text="Shown in the notification banner. Only applies when Notification Type is Notification or Both; on iOS also requires Mutable content." />
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="imageUrl"
                      placeholder="https://example.com/image.png"
                      value={formData?.imageUrl || ""}
                      onChange={(e) => handleChange("imageUrl", e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="clickAction">
                      Click Action / Deep Link
                      <InfoTooltip text="Sent as Android's notification click_action and also included in the data payload for custom in-app handling" />
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="clickAction"
                      placeholder="e.g. OPEN_ORDER or a deep link"
                      value={formData?.clickAction || ""}
                      onChange={(e) => handleChange("clickAction", e.target.value)}
                    />
                  </div>

                  <div className="platform-columns">
                    <div className="platform-column">
                      <h6>Android</h6>
                      <div className="form-group">
                        <label htmlFor="androidChannelId">
                          Channel ID
                          <InfoTooltip text="Android 8+ notification channel to post to. Must already exist in your app; if omitted, Android uses its default channel." />
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="androidChannelId"
                          placeholder="e.g. orders_channel"
                          value={formData?.androidChannelId || ""}
                          onChange={(e) => handleChange("androidChannelId", e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="androidPriority">
                          Priority
                          <InfoTooltip text="High wakes the device immediately to deliver the message; Normal is delivered when the device is next awake/idle." />
                        </label>
                        <select
                          className="form-control"
                          id="androidPriority"
                          value={formData?.androidPriority || "high"}
                          onChange={(e) => handleChange("androidPriority", e.target.value)}
                        >
                          <option value="high">High</option>
                          <option value="normal">Normal</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="androidSound">
                          Sound
                          <InfoTooltip text="Sound file name (without extension) bundled in your Android app's res/raw folder. Use 'default' for the system default." />
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="androidSound"
                          placeholder="default"
                          value={formData?.androidSound || ""}
                          onChange={(e) => handleChange("androidSound", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="platform-column">
                      <h6>iOS</h6>
                      <div className="form-group">
                        <label htmlFor="iosBadge">
                          Badge Count
                          <InfoTooltip text="Number shown on the app icon badge. Use 0 to clear it." />
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="iosBadge"
                          placeholder="1"
                          value={formData?.iosBadge ?? ""}
                          onChange={(e) => handleChange("iosBadge", e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="iosSound">
                          Sound
                          <InfoTooltip text="Sound file name bundled in your iOS app, or 'default' for the system sound." />
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="iosSound"
                          placeholder="default"
                          value={formData?.iosSound || ""}
                          onChange={(e) => handleChange("iosSound", e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="iosCategory">
                          Category
                          <InfoTooltip text="Matches a UNNotificationCategory identifier registered in your app, used for actionable notification buttons." />
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="iosCategory"
                          placeholder="e.g. ORDER_CATEGORY"
                          value={formData?.iosCategory || ""}
                          onChange={(e) => handleChange("iosCategory", e.target.value)}
                        />
                      </div>
                      <div className="form-group form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="iosMutableContent"
                          checked={!!formData?.iosMutableContent}
                          onChange={(e) => handleChange("iosMutableContent", e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="iosMutableContent">
                          Mutable content (required to display the image on iOS)
                        </label>
                      </div>
                    </div>
                  </div>
                </details>

                <div className="form-group">
                  <label>Custom Data (optional)</label>
                  <div className="data-mode-toggle" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm toggle-btn ${dataMode === "simple" ? "active" : ""}`}
                      onClick={() => setDataMode("simple")}
                    >
                      Key/Value
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm toggle-btn ${dataMode === "raw" ? "active" : ""}`}
                      onClick={() => setDataMode("raw")}
                    >
                      Raw JSON
                    </button>
                  </div>

                  {dataMode === "simple" ? (
                    <>
                      {dataFields.map((field, idx) => (
                        <div className="form-row data-field-row" key={idx}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="key"
                            value={field.key}
                            onChange={(e) => updateDataField(idx, "key", e.target.value)}
                          />
                          <input
                            type="text"
                            className="form-control"
                            placeholder="value"
                            value={field.value}
                            onChange={(e) => updateDataField(idx, "value", e.target.value)}
                          />
                          {dataFields.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm remove-field-btn"
                              onClick={() => removeDataField(idx)}
                              aria-label="Remove field"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      <button type="button" className="btn btn-sm add-field-btn" onClick={addDataField}>
                        + Add field
                      </button>
                    </>
                  ) : (
                    <>
                      <textarea
                        className="form-control"
                        rows={5}
                        placeholder='{"orderId": "123", "screen": "home"}'
                        value={rawData}
                        onChange={(e) => setRawData(e.target.value)}
                      />
                      <p className="error-msg">{error?.rawData}</p>
                    </>
                  )}
                </div>

                <button
                  type="button"
                  class="btn btn-primary btn-block"
                  onClick={onSubmit}
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Notification"}
                </button>
              </form>

              {result && (
                <div
                  className={`alert result-panel ${
                    result.success
                      ? result.results?.every((r) => r.success)
                        ? "alert-success"
                        : "alert-warning"
                      : "alert-danger"
                  }`}
                >
                  <p className="result-status">
                    {result.success
                      ? result.results?.every((r) => r.success)
                        ? "✅ Sent"
                        : "⚠️ Partial"
                      : "❌ Failed"}
                  </p>
                  <p>{result.message}</p>
                  {result.results && (
                    <ul className="token-results">
                      {result.results.map((r, idx) => (
                        <li key={idx}>
                          {r.success ? "✅" : "❌"}{" "}
                          <code>
                            {r.token.slice(0, 10)}...{r.token.slice(-6)}
                          </code>
                          {r.messageId && ` — ${r.messageId}`}
                          {r.errorMessage && ` — ${r.errorMessage}`}
                        </li>
                      ))}
                    </ul>
                  )}
                  {result.errorCode && (
                    <p>
                      Error code: <code>{result.errorCode}</code>
                    </p>
                  )}
                  {result.sentPayload && (
                    <details>
                      <summary>Payload sent to FCM</summary>
                      <pre>{JSON.stringify(result.sentPayload, null, 2)}</pre>
                    </details>
                  )}
                </div>
              )}

              {history.length > 0 && (
                <div className="history-panel">
                  <div className="history-header">
                    <h6>Recent sends</h6>
                    <button type="button" className="btn btn-sm add-field-btn" onClick={clearHistory}>
                      Clear history
                    </button>
                  </div>
                  <ul className="history-list">
                    {history.map((entry) => (
                      <li key={entry.id} className="history-item">
                        <div className="history-item-main">
                          <span>{entry.success ? "✅" : "❌"}</span>
                          <span className="history-title">{entry.title || "(no title)"}</span>
                          <span className="history-meta">
                            {entry.notificationType} · {entry.deviceTokens.length} token
                            {entry.deviceTokens.length === 1 ? "" : "s"}
                            {entry.dryRun ? " · dry run" : ""} ·{" "}
                            {new Date(entry.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="history-item-actions">
                          <button
                            type="button"
                            className="btn btn-sm add-field-btn"
                            onClick={() => reuseHistoryEntry(entry)}
                          >
                            Reuse
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm remove-field-btn"
                            onClick={() => removeHistoryEntry(entry.id)}
                            aria-label="Remove from history"
                          >
                            ✕
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* <div class="footer">
        <p>😀Just Growing With The Flow😀</p>
      </div> */}
    </div>
  );
}

export default App;
